import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BsChevronDown, BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import {
  MONTHS_SHORT,
  getMonthPeriod,
  getYearPeriod,
  getCurrentQuarterPeriod,
} from '../../../core/utils/financialPeriod';
import { loadEntryMonths } from '../../../core/utils/financialEntry';

const monthKey = (year, monthIdx) => `${year}-${String(monthIdx + 1).padStart(2, '0')}`;

// Seletor de período do dashboard: setas trocam o mês direto; o painel navega
// por ano com a grade de meses (ponto verde = mês com lançamentos).
const PeriodSelector = ({ companyId, selectedPeriod, onChange }) => {
  const currentYear = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const [entryMonths, setEntryMonths] = useState([]);
  const [panelYear, setPanelYear] = useState(currentYear);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Recarrega a cada abertura pra refletir lançamentos criados na sessão.
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    loadEntryMonths(companyId).then((months) => {
      if (!cancelled) setEntryMonths(months);
    });
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const monthSet = useMemo(() => new Set(entryMonths), [entryMonths]);

  const selFrom = selectedPeriod?.range?.from || '';
  const selYear = selFrom ? Number(selFrom.slice(0, 4)) : currentYear;
  const selMonthIdx = selFrom ? Number(selFrom.slice(5, 7)) - 1 : new Date().getMonth();

  const minYear = entryMonths.length
    ? Number(entryMonths[0].slice(0, 4))
    : currentYear;
  const maxYear = Math.max(
    currentYear,
    entryMonths.length ? Number(entryMonths[entryMonths.length - 1].slice(0, 4)) : currentYear
  );

  const togglePanel = () => {
    if (!open) setPanelYear(selYear || currentYear);
    setOpen(!open);
  };

  const stepMonth = (delta) => {
    const d = new Date(selYear, selMonthIdx + delta, 1);
    onChange(getMonthPeriod(d.getFullYear(), d.getMonth()));
  };

  const selectOption = (option) => {
    onChange(option);
    setOpen(false);
  };

  return (
    <div className="fd-period-selector" ref={ref}>
      <button type="button" className="fd-period-arrow" title="Mês anterior" onClick={() => stepMonth(-1)}>
        <BsChevronLeft />
      </button>
      <button
        className="fd-topbar-btn"
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={togglePanel}
      >
        <span>{selectedPeriod?.label}</span>
        <BsChevronDown className="fd-topbar-btn-icon" />
      </button>
      <button type="button" className="fd-period-arrow" title="Próximo mês" onClick={() => stepMonth(1)}>
        <BsChevronRight />
      </button>

      {open && (
        <div className="fd-period-panel">
          <div className="fd-period-panel-year">
            <button
              type="button"
              disabled={panelYear <= minYear}
              onClick={() => setPanelYear((y) => y - 1)}
            >
              <BsChevronLeft />
            </button>
            <span>{panelYear}</span>
            <button
              type="button"
              disabled={panelYear >= maxYear}
              onClick={() => setPanelYear((y) => y + 1)}
            >
              <BsChevronRight />
            </button>
          </div>

          <div className="fd-period-panel-grid">
            {MONTHS_SHORT.map((label, idx) => {
              const key = monthKey(panelYear, idx);
              const isActive = selFrom.slice(0, 7) === key;
              const hasData = monthSet.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`fd-month-btn${isActive ? ' active' : ''}${hasData ? ' has-data' : ''}`}
                  title={hasData ? 'Há lançamentos neste mês' : undefined}
                  onClick={() => selectOption(getMonthPeriod(panelYear, idx))}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="fd-period-panel-footer">
            <button
              type="button"
              className="fd-topbar-dropdown-item"
              onClick={() => selectOption(getYearPeriod(panelYear))}
            >
              Ano {panelYear} completo
            </button>
            <button
              type="button"
              className="fd-topbar-dropdown-item"
              onClick={() => selectOption(getCurrentQuarterPeriod())}
            >
              Trimestre atual
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;
